import sportsApi from './sportsApi';

export type AttributionMode = 'team' | 'league';
export type FlatAttributionMap = Record<string, string>;
export type NamespacedAttributionMap = {
    census?: FlatAttributionMap;
    edge?: FlatAttributionMap;
};
// What is actually found in stored preferences: the namespaced shape, the legacy
// flat clientId→mode shape, or a mix of the two (see readEdgeAttributionMode).
export type StoredAttributionMap = Record<
    string,
    string | FlatAttributionMap | undefined
>;

// Returns true when rightsholder_attribution carries at least one app namespace
// (a nested object) rather than being purely the legacy flat clientId→mode map.
// Note a map can be BOTH: a namespace plus leftover flat keys.
export const isNamespacedAttributionMap = (
    rha: object,
): rha is NamespacedAttributionMap =>
    Object.values(rha).some(v => typeof v === 'object' && v !== null);

const EDGE_NAMESPACE = 'edge';

const normaliseMode = (value: unknown): AttributionMode =>
    value === 'league' ? 'league' : 'team';

// A legacy entry is a clientId keyed straight onto the map. Anything else with a
// string value is not ours to migrate, and both the migration and the check for
// whether one is needed have to agree on that — otherwise a stray key would be
// re-patched on every login.
const isLegacyFlatEntry = (key: string, value: unknown): boolean =>
    typeof value === 'string' && /^\d+$/.test(key);

/**
 * Reads the Edge attribution mode for one client out of a stored
 * rightsholder_attribution map.
 *
 * The single source of truth for "which mode is this client in", shared by the
 * render path (useRightsholderAttribution) and the query path
 * (getUseRightsholderAttribution) so the switch can never contradict the
 * numbers it is describing.
 *
 * Handles all three shapes that exist in stored preferences:
 *  - namespaced:  { edge: { '4': 'league' } }
 *  - legacy flat: { '4': 'league' }
 *  - mixed:       { '4': 'league', edge: { '9': 'team' } }
 * A mixed map is produced whenever something writes a namespace into a legacy
 * map, because the API's RFC 7396 merge keeps the flat keys it was not asked to
 * remove. The flat entry is therefore a fallback, never invisible.
 *
 * Anything that is not exactly 'league' reads as team, matching the only value
 * the API acts on.
 */
export const readEdgeAttributionMode = (
    rha: StoredAttributionMap | null | undefined,
    clientId: number | string | null | undefined,
): AttributionMode | null => {
    if (!rha || clientId == null) return null;

    const clientKey = String(clientId);
    const namespaced = isNamespacedAttributionMap(rha)
        ? (rha as NamespacedAttributionMap)[EDGE_NAMESPACE]?.[clientKey]
        : undefined;
    const legacyFlat = rha[clientKey];

    // An explicit namespaced value wins; a leftover flat value is the fallback.
    if (namespaced != null) return normaliseMode(namespaced);
    if (typeof legacyFlat === 'string') return normaliseMode(legacyFlat);
    return null;
};

/**
 * Builds the rightsholder_attribution patch that moves one client's Edge mode.
 *
 * Minimal by design: the API applies an RFC 7396 merge, so sending only the keys
 * being changed lets a concurrent write for another client — another tab, another
 * device — survive. Sending the whole map back (read from this tab's possibly
 * stale localStorage) overwrites those writes instead of merging with them.
 *
 * Legacy flat entries are carried into the edge namespace and then deleted (a
 * null value is an RFC 7396 delete), so a legacy or mixed map converges on the
 * namespaced shape rather than keeping flat keys that nothing reads. A flat
 * entry whose client already has an edge value is only deleted: the edge value
 * is the newer one and the one readEdgeAttributionMode already serves.
 */
export const buildAttributionPatch = (
    rha: StoredAttributionMap | null | undefined,
    clientId: number | string,
    mode: AttributionMode,
): Record<string, unknown> => {
    const clientKey = String(clientId);
    const edge: FlatAttributionMap = { [clientKey]: mode };
    const deletions: Record<string, null> = {};
    const existingEdge =
        rha && isNamespacedAttributionMap(rha)
            ? (rha as NamespacedAttributionMap)[EDGE_NAMESPACE]
            : undefined;

    Object.entries(rha ?? {}).forEach(([key, value]) => {
        // Namespaces (edge, census) are left untouched.
        if (!isLegacyFlatEntry(key, value)) return;
        if (key !== clientKey && existingEdge?.[key] == null) {
            edge[key] = normaliseMode(value);
        }
        deletions[key] = null;
    });

    return { ...deletions, [EDGE_NAMESPACE]: edge };
};

// Whether a stored map still holds legacy flat entries that readers only see
// through the fallback above.
export const hasLegacyFlatEntries = (
    rha: StoredAttributionMap | null | undefined,
): boolean =>
    Object.entries(rha ?? {}).some(([key, value]) =>
        isLegacyFlatEntry(key, value),
    );

// RFC 7396 merge patch, matching what the API applies server-side: objects merge
// recursively, a null deletes the key, anything else replaces.
export const applyMergePatch = (target: unknown, patch: unknown): unknown => {
    if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) {
        return patch;
    }
    const merged: Record<string, unknown> =
        target && typeof target === 'object' && !Array.isArray(target)
            ? { ...(target as Record<string, unknown>) }
            : {};
    Object.entries(patch as Record<string, unknown>).forEach(([key, value]) => {
        if (value === null) delete merged[key];
        else merged[key] = applyMergePatch(merged[key], value);
    });
    return merged;
};

type UserPreferences = {
    rightsholder_attribution?: FlatAttributionMap | NamespacedAttributionMap;
    [key: string]: any;
};

type UserPreferencesResponse = {
    preferences: UserPreferences;
};

type UpdateUserPreferencesParams = {
    userId: number;
    preferences: UserPreferences;
};

/**
 * Update user preferences via API and localStorage
 * Use this when user manually changes preferences (e.g., via a switch/toggle)
 */
export const updateUserPreferences = async ({
    userId,
    preferences,
}: UpdateUserPreferencesParams): Promise<UserPreferences> => {
    try {
        // Send PATCH request to API
        const response: UserPreferencesResponse = await sportsApi
            .url(`/users/${userId}/preferences`)
            .query({
                // removes client id from the request
                noClientId: true,
            })
            .json({ preferences })
            .patch()
            .json();

        // Update localStorage after successful API call
        const storedUser = localStorage.getItem('mvc-user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            const effective = response.preferences || preferences;
            const existingPreferences = parsedUser.userPreferences || {};
            // The response is the API's own re-read of the merged row, so it is
            // authoritative — including for keys this caller did not send. Only
            // when it carries no attribution map do we apply our patch locally,
            // with the same RFC 7396 semantics the API used (nulls delete).
            const serverRha = response.preferences?.rightsholder_attribution;
            const patchedRha =
                preferences.rightsholder_attribution !== undefined
                    ? applyMergePatch(
                          existingPreferences.rightsholder_attribution,
                          preferences.rightsholder_attribution,
                      )
                    : existingPreferences.rightsholder_attribution;
            const rightsholderAttribution = serverRha ?? patchedRha;
            parsedUser.userPreferences = {
                ...existingPreferences,
                ...effective,
                // Absent on unrelated updates (a language change), which must
                // not drop the attribution map that is already stored.
                ...(rightsholderAttribution !== undefined && {
                    rightsholder_attribution: rightsholderAttribution,
                }),
            };
            localStorage.setItem('mvc-user', JSON.stringify(parsedUser));
            // Dispatch custom event to notify other components in the same tab
            window.dispatchEvent(
                new CustomEvent('mvc-user-updated', {
                    detail: { preferences: parsedUser.userPreferences },
                }),
            );
        }

        return response.preferences || preferences;
    } catch (error) {
        console.error('Error updating user preferences:', error);
        throw error;
    }
};
